import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
// The same Owner picker and map picker the desktop New Bid uses (Pat, Sep 24 2026: on the
// phone the Owner didn't fill in and the Location didn't tie to the map).
import { CompanyCombo, buildCompanies, LocationPickerModal } from './admin/AdminBidDetail';

const STATUS = {
  draft:     { label: 'Upcoming',  bg: '#f3f4f6', color: '#374151' },
  submitted: { label: 'Submitted', bg: '#dbeafe', color: '#1e40af' },
  awarded:   { label: 'Awarded',   bg: '#dcfce7', color: '#166534' },
  lost:      { label: 'Lost',      bg: '#fee2e2', color: '#991b1b' },
};
const CHIPS = [['active', 'Active'], ['draft', 'Upcoming'], ['submitted', 'Submitted'], ['awarded', 'Awarded'], ['lost', 'Lost']];

// Sensible defaults so a phone-started bid opens cleanly on the desktop to finish pricing.
const NEW_BID = {
  job_name: '', client_name: '', client_id: null, client_contact: '', client_address1: '', client_address2: '', client_phone: '', client_fax: '', client_email: '',
  job_location: '', bid_date: '', bid_time: '',
  bid_type: 'Lump Sum', job_type: 'Sub-Contractor', bid_pm_id: '', scope: '',
  overhead_material_pct: 0, overhead_labor_pct: 0, profit_pct: 20, sales_tax_pct: 8.265, supervision_pct: 6, labor_rate: 95,
};
const BID_TYPES = [['Lump Sum', 'Unit Price'], ['Time and Material', 'Time and Material'], ['Vertical Drilling', 'Vertical Drilling'], ['Directional Drilling', 'Directional Drilling']];

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(String(d).length <= 10 ? d + 'T12:00:00' : d);
  return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Mobile Bids list for PMs — status chips + vertical cards. Tapping a card opens
// the bid detail in MOBILE view (/sup/bids/:id → AdminBidDetailInner embedded, no
// desktop sidebar), keeping the PM inside the mobile app.
export default function SupBids() {
  const navigate = useNavigate();
  const { worker } = useAuth();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  // Stacks with the status chips: my submitted bids, my active ones.
  const [mine, setMine] = useState(false);
  const [search, setSearch] = useState('');
  const [pms, setPms] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(NEW_BID);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  // Owner picker: customers + vendors, the same list the desktop offers; picking one fills
  // the contact, address and phone. A company typed fresh is added on the spot.
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [extraClients, setExtraClients] = useState([]);
  const [extraVendors, setExtraVendors] = useState([]);
  const [pickMap, setPickMap] = useState(false);
  const companies = buildCompanies([...clients, ...extraClients], []);   // customers only (Ryan, Oct 1 2026)
  function pickCompany(co) {
    if (!co) { setForm(f => ({ ...f, client_id: null, client_name: '', client_address1: '', client_address2: '', client_phone: '', client_fax: '', client_contact: '', client_email: '' })); return; }
    setForm(f => ({ ...f, client_id: co.clientId || null, client_name: co.name, client_address1: co.address1 || '', client_address2: co.address2 || '', client_phone: co.phone || '', client_fax: co.fax || '', client_contact: co.contact || '', client_email: co.email || '' }));
  }

  useEffect(() => {
    apiFetch('/bids').then(d => setBids(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setLoading(false));
    apiFetch('/users').then(u => setPms((Array.isArray(u) ? u : []).filter(x => x.role === 'ProjectManager'))).catch(() => {});
    apiFetch('/clients').then(c => setClients(Array.isArray(c) ? c : [])).catch(() => {});
    apiFetch('/vendors').then(v => setVendors(Array.isArray(v) ? v : [])).catch(() => {});
  }, []);

  async function createBid() {
    if (!form.job_name.trim()) { setErr('Job name is required'); return; }
    setSaving(true); setErr('');
    try {
      const bid = await apiFetch('/bids', { method: 'POST', body: JSON.stringify({ ...form, bid_pm_id: form.bid_pm_id || null }) });
      setBids(b => [bid, ...b]); setFilter('draft'); setShowNew(false); setForm(NEW_BID);
    } catch (e) { setErr(e?.error || e?.message || 'Could not create bid'); }
    setSaving(false);
  }

  const q = search.trim().toLowerCase();
  const inFilter = b => filter === 'active' ? (b.status === 'draft' || b.status === 'submitted') : b.status === filter;
  const count = key => key === 'active' ? bids.filter(b => b.status === 'draft' || b.status === 'submitted').length : bids.filter(b => b.status === key).length;
  const isMine = b => b.bid_pm_id && b.bid_pm_id === worker?.id;
  const shown = bids.filter(b => inFilter(b) && (!mine || isMine(b))
    && (!q || `${b.job_name || ''} ${b.client_name || ''} ${b.job_location || ''}`.toLowerCase().includes(q)));

  const chip = on => ({ whiteSpace: 'nowrap', padding: '8px 14px', borderRadius: 20, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', background: on ? '#111' : '#f3f4f6', color: on ? '#e87722' : '#374151' });
  const badge = { fontSize: 11, color: '#6b7280', background: '#f3f4f6', padding: '3px 8px', borderRadius: 10, fontWeight: 600 };

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <div style={{ background: '#111', color: '#fff', paddingTop: 'max(env(safe-area-inset-top), 14px)', paddingBottom: 14, paddingLeft: 16, paddingRight: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', padding: 0, color: '#e87722', lineHeight: 1 }}>‹</button>
        <div style={{ fontWeight: 800, fontSize: 17, flex: 1 }}>Bids</div>
        <button onClick={() => { setForm(NEW_BID); setErr(''); setShowNew(true); }} style={{ background: '#e87722', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>+ New Bid</button>
      </div>

      <div style={{ padding: '12px 12px 0', maxWidth: 540, margin: '0 auto' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search job, client, location…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 16, marginBottom: 12 }} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingBottom: 12 }}>
          {CHIPS.map(([key, label]) => <button key={key} onClick={() => setFilter(key)} style={chip(filter === key)}>{label} ({count(key)})</button>)}
          {/* Only shown to someone who has bids of their own. */}
          {bids.some(isMine) && (
            <button onClick={() => setMine(v => !v)}
              style={{ ...chip(mine), background: mine ? '#1e3a5f' : '#f3f4f6', color: mine ? '#fff' : '#374151' }}>
              Mine ({bids.filter(b => inFilter(b) && isMine(b)).length})
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: '0 12px 90px', maxWidth: 540, margin: '0 auto' }}>
        {loading ? (
          <p style={{ color: '#9ca3af', textAlign: 'center', paddingTop: 30 }}>Loading…</p>
        ) : shown.length === 0 ? (
          <p style={{ color: '#9ca3af', textAlign: 'center', paddingTop: 30 }}>No bids here.</p>
        ) : shown.map(b => {
          const sm = STATUS[b.status] || { label: b.status, bg: '#f3f4f6', color: '#374151' };
          return (
            <div key={b.id} onClick={() => navigate(`/sup/bids/${b.id}`)}
              style={{ background: '#fff', border: '1.5px solid #ececec', borderLeft: '5px solid #1e3a5f', borderRadius: 12, padding: '14px 16px', marginBottom: 10, cursor: 'pointer', WebkitTapHighlightColor: 'rgba(30,58,95,0.12)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#111', flex: 1, minWidth: 0 }}>{b.job_name || '(untitled bid)'}</div>
                <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '3px 10px', background: sm.bg, color: sm.color, whiteSpace: 'nowrap' }}>{sm.label}</span>
                <span style={{ color: '#c7c7c7', fontSize: 18, fontWeight: 700, marginLeft: 2 }}>›</span>
              </div>
              {b.client_name && <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>{b.client_name}</div>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                {b.job_location && <span style={badge}>📍 {b.job_location}</span>}
                {b.bid_date && <span style={badge}>📅 {fmtDate(b.bid_date)}</span>}
                {b.bid_type && <span style={badge}>{b.bid_type}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {showNew && (() => {
        const inp = { width: '100%', boxSizing: 'border-box', padding: '11px 12px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 16, color: '#111', background: '#fff' };
        const lbl = { display: 'block', fontSize: 11, fontWeight: 800, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 };
        const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 4000, display: 'flex', alignItems: 'flex-end' }} onClick={() => setShowNew(false)}>
            <div style={{ background: '#fff', width: '100%', borderRadius: '18px 18px 0 0', padding: '20px 18px 40px', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#111' }}>New Bid</div>
                <button onClick={() => setShowNew(false)} style={{ background: 'none', border: 'none', fontSize: 26, cursor: 'pointer', color: '#999' }}>×</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div><label style={lbl}>Job Name *</label><input value={form.job_name} onChange={e => set('job_name', e.target.value)} style={inp} placeholder="e.g. RTC Mill St Signal" /></div>
                <div>
                  <label style={lbl}>Owner</label>
                  <CompanyCombo companies={companies} value={form.client_name} placeholder="Type or pick a customer, or type a name…" allowCreate customersOnly allowOneOff
                    onCreated={(kind, rec) => { if (kind === 'client') setExtraClients(prev => [...prev, rec]); else setExtraVendors(prev => [...prev, rec]); }}
                    onSelect={pickCompany} style={inp} />
                  {form.client_name && (form.client_contact || form.client_phone || form.client_address1) && (
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{[form.client_contact, form.client_phone, form.client_address1].filter(Boolean).join(' · ')}</div>
                  )}
                </div>
                <div>
                  <label style={lbl}>Location</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input value={form.job_location} onChange={e => set('job_location', e.target.value)} style={{ ...inp, flex: 1 }} placeholder="Address, or pick it on the map" />
                    <button type="button" onClick={() => setPickMap(true)} style={{ padding: '0 14px', borderRadius: 10, border: '1.5px solid #1e3a5f', background: '#eff6ff', color: '#1e3a5f', fontSize: 13, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>📍 Map</button>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}><label style={lbl}>Bid Type</label><select value={form.bid_type} onChange={e => set('bid_type', e.target.value)} style={inp}>{BID_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
                  <div style={{ flex: 1 }}><label style={lbl}>Job Type</label><select value={form.job_type} onChange={e => set('job_type', e.target.value)} style={inp}><option value="Sub-Contractor">Sub-Contractor</option><option value="Prime">Prime</option></select></div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}><label style={lbl}>Bid Date</label><input type="date" value={form.bid_date} onChange={e => set('bid_date', e.target.value)} style={inp} /></div>
                  <div style={{ width: 130 }}><label style={lbl}>Bid Time</label><input type="time" value={form.bid_time} onChange={e => set('bid_time', e.target.value)} style={inp} /></div>
                </div>
                <div><label style={lbl}>Estimator / PM</label><select value={form.bid_pm_id} onChange={e => set('bid_pm_id', e.target.value)} style={inp}><option value="">— None —</option>{pms.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}</select></div>
                <div><label style={lbl}>Scope (optional)</label><textarea value={form.scope} onChange={e => set('scope', e.target.value)} rows={3} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} placeholder="What's included…" /></div>
                {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
                <button onClick={createBid} disabled={saving} style={{ width: '100%', padding: '14px 0', background: saving ? '#ccc' : '#e87722', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>{saving ? 'Creating…' : 'Create Bid'}</button>
                <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>Finish pricing &amp; line items on the office app.</div>
              </div>
            </div>
            {pickMap && (
              // The picker sits outside the sheet panel; a tap inside it must not reach the
              // backdrop, which would close the whole New Bid sheet.
              <div onClick={e => e.stopPropagation()}>
                <LocationPickerModal value={form.job_location}
                  onConfirm={({ address }) => { if (address) set('job_location', address); setPickMap(false); }}
                  onClose={() => setPickMap(false)} />
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
